import { useState } from "react";
import { local_backend } from "../config";
import formatMessage from "../utils/formatMessage";

// Sends a message through our backend proxy (which talks to OpenAI server-side
// and returns the completed assistant reply). This replaces the previous direct
// browser -> api.openai.com streaming call, which was blocked by CORS in
// production and leaked the OpenAI API key to the client. The reply arrives in
// one piece (no token-by-token streaming), so there is no live preview message.
const useSendMessage = (threadId, assistantId, setMessages) => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [statusMessage, setStatusMessage] = useState("");
    const [analyzingFiles, setAnalyzingFiles] = useState(false);
    // Kept for interface compatibility with ChatUI; unused without streaming.
    const [newMessages] = useState([]);

    const sendMessage = async (text) => {
        if (!threadId) {
            setError("Chat initialization failed. Please refresh the page.");
            return;
        }

        setError(null);
        setIsLoading(true);
        setAnalyzingFiles(true);
        setStatusMessage("The assistant is thinking...");

        // Add user message to UI immediately
        setMessages((prev) => [
            ...prev,
            { id: Date.now(), role: "user", content: text, originalContent: text },
        ]);

        try {
            // Absolute backend route: /api/assistants/threads/{threadId}/messages
            const response = await fetch(
                `${local_backend}assistants/threads/${threadId}/messages`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ content: text }),
                }
            );

            if (!response.ok) {
                const body = await response.text().catch(() => "");
                throw new Error(`Request failed (${response.status})${body ? `: ${body}` : ""}`);
            }

            const data = await response.json();
            // Be tolerant of either JSON casing (StatusCode/isSuccess/Message).
            const succeeded = data?.isSuccess ?? data?.IsSuccess ?? true;
            const reply = (data?.Message ?? data?.message) || "No response available.";

            if (!succeeded) {
                // Surface the backend's own message (e.g. "Run failed.",
                // "Maximum polling time exceeded.") so failures are diagnosable.
                throw new Error(reply);
            }

            setMessages((prev) => [
                ...prev,
                {
                    role: "assistant",
                    content: formatMessage(reply),
                    id: new Date(),
                    originalContent: reply,
                },
            ]);
        } catch (err) {
            setError(err?.message || "Failed to send message. Please try again.");
            console.error("Message sending error:", err);
        } finally {
            setIsLoading(false);
            setAnalyzingFiles(false);
            setStatusMessage("");
        }
    };

    return { sendMessage, isLoading, error, statusMessage, analyzingFiles, newMessages };
};

export default useSendMessage;
